import { inject, Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, CanActivateFn, CanDeactivateFn } from '@angular/router';

import { AuthenticationService } from '../services/AuthenticationService';
import { from, map, Observable, of, switchMap } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../shared/dialog/confirmation-dialog.component';

@Injectable({ providedIn: 'root' })
export class PermissionsService {
    constructor(private router: Router, private authenticationService: AuthenticationService, private dialog: MatDialog) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        const currentUser = this.authenticationService.currentUserValue;
        if (currentUser) {
            // check if route is restricted by role
            if (route.data.roles && route.data.roles.indexOf(currentUser.role.name) === -1) {
                // role not authorised so redirect to home page
                this.router.navigate(['/']);
                return false;
            }
            if (route.data.anonymousOnly){
                this.router.navigate(['/']);
                return false;
            }
            if(route.data.permissions){
                if(route.data.permissions.indexOf('canCreateMoovs') >-1){
                    if(!this.authenticationService.customerContext?.canCreateMoovs){
                        this.router.navigate(['/']);
                        return false;
                    };
                }
            }
            // authorised so return true
            return true;
        }
        else{
            if (route.data.anonymousOnly){
                return true;
            }
            else{
                // not logged in so redirect to login page with the return url
                this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
                return false;
            }
        }
      
    }

    //component needs to implement interface ComponentCanDeactivate
    canDeactivate(component: ComponentCanDeactivate, currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot, nextState: RouterStateSnapshot): Observable<boolean> {
        if (component.hasUnsavedChanges) {
            // If the component provides its own deactivation dialog, use it. For example, the wizard has a custom dialog with three options.
            if (component.openDeactivateDialog) {
                return component.openDeactivateDialog();
            }

            const dialogConfig = new MatDialogConfig();
            dialogConfig.disableClose = true;
            dialogConfig.autoFocus = true;
            
            const title = "Warning";
            // Use custom message from component if provided, otherwise use default
            const message = component.getPendingChangesMessage?.() || "You have unsaved changes. Press Cancel to go back and save these changes, or OK to lose these changes.";
            const bottonTexts = component.getPendingChangesButtonTexts?.() || { ok: "OK", cancel: "Cancel" }
            dialogConfig.data = {
                title: title, 
                message: message, 
                buttonText: bottonTexts
            };
        
            return this.dialog.open(ConfirmationDialogComponent, dialogConfig)
            .afterClosed()
            .pipe(
                switchMap(value => {
                    let canDeactivate = value as boolean;
                    // Notify component of the decision
                    if (component.onDeactivateDecision) {
                        const result = component.onDeactivateDecision(canDeactivate);
                        // Handle async finalization
                        if (result instanceof Observable) {
                            return result.pipe(map((r) => canDeactivate=r));
                        } 
                        else if (result instanceof Promise) {
                            return from(result).pipe(map((r) => canDeactivate=r));
                        }
                        else canDeactivate=result;
                    }
                    return of(canDeactivate);
                })
            );
        }
        return of(true);
    }
}

export const AuthGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean => {
    return inject(PermissionsService).canActivate(next, state);
}

export const PendingChangesGuard: CanDeactivateFn<ComponentCanDeactivate> = (component: ComponentCanDeactivate, currentRoute: ActivatedRouteSnapshot,  currentState: RouterStateSnapshot, nextState: RouterStateSnapshot): Observable<boolean> => {
  return inject(PermissionsService).canDeactivate(component, currentRoute, currentState, nextState);
}

export interface ComponentCanDeactivate {
  hasUnsavedChanges: boolean;
  getPendingChangesMessage?(): string;
  getPendingChangesButtonTexts?(): {ok:string, cancel:string};
  onDeactivateDecision?(canDeactivate: boolean): boolean | Observable<boolean> | Promise<boolean>;
  /** Override to provide a fully custom deactivation dialog instead of the default ConfirmationDialogComponent. */
  openDeactivateDialog?(): Observable<boolean>;
}